import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { workspaceAPI } from '../services/api';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { 
  FiPlus, FiEdit3, FiTrash2, FiUsers, FiSettings,
  FiMoreVertical, FiFolder, FiStar
} = FiIcons;

const Workspaces = () => {
  const { workspaces, currentWorkspace, switchWorkspace, fetchWorkspaces } = useWorkspace();
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newWorkspaceName, setNewWorkspaceName] = useState('');
  const [loading, setLoading] = useState(false);

  const handleCreateWorkspace = async (e) => {
    e.preventDefault();
    if (!newWorkspaceName.trim()) return;

    setLoading(true);
    try {
      await workspaceAPI.createWorkspace(newWorkspaceName);
      setNewWorkspaceName('');
      setShowCreateModal(false);
      fetchWorkspaces();
    } catch (error) {
      console.error('Failed to create workspace:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteWorkspace = async (workspaceId) => {
    if (!confirm('Are you sure you want to delete this workspace?')) return;

    try {
      await workspaceAPI.deleteWorkspace(workspaceId);
      fetchWorkspaces();
    } catch (error) {
      console.error('Failed to delete workspace:', error);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Workspaces</h1>
          <p className="mt-1 text-sm text-gray-500">
            Organize your QR codes and collaborate with your team
          </p>
        </div>
        
        <button
          onClick={() => setShowCreateModal(true)}
          className="mt-3 sm:mt-0 inline-flex items-center px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
        >
          <SafeIcon icon={FiPlus} className="w-4 h-4 mr-2" />
          Create Workspace
        </button>
      </div>

      {/* Workspaces Grid */}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {workspaces.map((workspace, index) => (
          <motion.div
            key={workspace.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: index * 0.1 }}
            className={`bg-white p-6 rounded-xl shadow-sm border-2 transition-all hover:shadow-md ${
              currentWorkspace?.id === workspace.id 
                ? 'border-blue-500 bg-blue-50' 
                : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <div className="flex items-start justify-between mb-4">
              <div className="flex items-center">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg flex items-center justify-center">
                  <SafeIcon icon={FiFolder} className="w-6 h-6 text-white" />
                </div>
                {currentWorkspace?.id === workspace.id && (
                  <div className="ml-2 p-1 bg-blue-100 rounded-full">
                    <SafeIcon icon={FiStar} className="w-4 h-4 text-blue-600" />
                  </div>
                )}
              </div>
              
              <div className="relative">
                <button className="p-2 hover:bg-gray-100 rounded-lg">
                  <SafeIcon icon={FiMoreVertical} className="w-4 h-4 text-gray-400" />
                </button>
              </div>
            </div>

            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-1">
                {workspace.name}
              </h3>
              <p className="text-sm text-gray-500">
                {workspace.description || 'No description'}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 mb-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{workspace.qrCount || 0}</p>
                <p className="text-xs text-gray-500">QR Codes</p>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-gray-900">{workspace.memberCount || 1}</p>
                <p className="text-xs text-gray-500">Members</p>
              </div>
            </div>

            <div className="flex space-x-2">
              {currentWorkspace?.id !== workspace.id ? (
                <button
                  onClick={() => switchWorkspace(workspace)}
                  className="flex-1 px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Switch to
                </button>
              ) : (
                <button className="flex-1 px-3 py-2 bg-blue-100 text-blue-700 text-sm font-medium rounded-lg cursor-default">
                  Current
                </button>
              )}
              
              <button className="px-3 py-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors">
                <SafeIcon icon={FiEdit3} className="w-4 h-4" />
              </button>
              
              {workspace.role === 'owner' && workspaces.length > 1 && (
                <button 
                  onClick={() => handleDeleteWorkspace(workspace.id)}
                  className="px-3 py-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                </button>
              )}
            </div>
          </motion.div>
        ))}

        {/* Create Workspace Card */}
        <motion.button
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: workspaces.length * 0.1 }}
          onClick={() => setShowCreateModal(true)}
          className="bg-white p-6 rounded-xl shadow-sm border-2 border-dashed border-gray-300 hover:border-blue-400 hover:bg-blue-50 transition-all group"
        >
          <div className="flex flex-col items-center justify-center h-full min-h-[200px]">
            <div className="w-12 h-12 bg-gray-100 group-hover:bg-blue-100 rounded-lg flex items-center justify-center mb-4 transition-colors">
              <SafeIcon icon={FiPlus} className="w-6 h-6 text-gray-400 group-hover:text-blue-500" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1">
              Create Workspace
            </h3>
            <p className="text-sm text-gray-500 text-center">
              Start a new workspace to organize your QR codes
            </p>
          </div>
        </motion.button>
      </div>

      {/* Create Workspace Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-white rounded-xl p-6 w-full max-w-md mx-4"
          >
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Create New Workspace
            </h3>
            
            <form onSubmit={handleCreateWorkspace}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Workspace Name
                </label>
                <input
                  type="text"
                  value={newWorkspaceName}
                  onChange={(e) => setNewWorkspaceName(e.target.value)}
                  placeholder="Enter workspace name"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setNewWorkspaceName('');
                  }}
                  className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || !newWorkspaceName.trim()}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Creating...' : 'Create'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Workspaces;